import {
    Card,
    Colors,
    Heading,
    Icon,
    MultiSelectOutlined,
    RadioGroup,
    Text,
    type OptionProps,
    type RadioGroupOption,
} from '@amelio/ui-library';
import { AnimatePresence, motion } from 'motion/react';

import { AttributeSelectList } from '../AttributeSelectList/AttributeSelectList';

import type {
    OnSelectCompanyAttributeOption,
    SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import type { CompanyAttributeMap } from '@/utils/company';

import { RadioCard } from '@/components/RadioCard/RadioCard';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import {
    CycleAudienceType,
    type CycleAudienceTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';
import { decodeAndSanitize } from '@/utils/htmlSanitizer';
import { getOptionLabelText } from '@/utils/optionLabel';

export interface DefineAudienceContentProps {
    isLoading: boolean;
    options: RadioGroupOption[];
    selectedOption: CycleAudienceTypeValue;
    companyAttributes: OptionProps[];
    selectedCompanyAttributes: CompanyAttributeMap;
    filters: SelectedFilters;
    setSelectedOption: (option: CycleAudienceTypeValue) => void;
    onSelectCompanyAttribute: (value: string) => void;
    onSelectCompanyAttributeOption: ({ value, attribute }: OnSelectCompanyAttributeOption) => void;
}

export const DefineAudienceContent = ({
    isLoading,
    options = [],
    selectedOption,
    companyAttributes,
    selectedCompanyAttributes,
    filters,
    setSelectedOption,
    onSelectCompanyAttribute,
    onSelectCompanyAttributeOption,
}: DefineAudienceContentProps) => {
    const { t } = useAmelioTranslations();

    const getDescription = (value: string) => {
        return value === CycleAudienceType.ENTIRE_COMPANY
            ? t('cycleEvaluationDefineAudience.content.entireCompany.description')
            : t('cycleEvaluationDefineAudience.content.filterByAttributes.description');
    };

    const selectCompanyAttributeOption = ({ value, attribute }: OnSelectCompanyAttributeOption) => {
        onSelectCompanyAttributeOption({ value, attribute });
    };

    const selectedCompanyAttributeOptions = Object.values(selectedCompanyAttributes ?? {});

    return (
        <Card
            padding={'2xl'}
            borderRadius={'2xl'}
        >
            <Heading level={4}>{t('cycleEvaluationDefineAudience.content.heading')}</Heading>

            <Text className={'mt-3'}>{t('cycleEvaluationDefineAudience.content.description')}</Text>

            {isLoading ? (
                <Skeleton className={'mt-9 flex flex-row gap-4'}>
                    <Bone className={'h-24 w-64'} />

                    <Bone className={'h-24 w-64'} />
                </Skeleton>
            ) : (
                <>
                    <RadioGroup
                        className={'mt-9 w-fit'}
                        options={options}
                        name={'audience'}
                        columns={2}
                        value={selectedOption}
                        renderOption={({ option, ...props }) => (
                            <RadioCard
                                {...props}
                                option={option}
                                description={getDescription(option.value)}
                            />
                        )}
                        onChange={(value) => setSelectedOption(value as CycleAudienceTypeValue)}
                    />

                    <AnimatePresence>
                        {selectedOption === CycleAudienceType.FILTER_BY_ATTRIBUTES && (
                            <motion.div
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                className={'mt-12 mb-8 flex w-full flex-wrap gap-4'}
                            >
                                <div className={'shrink-0'}>
                                    <MultiSelectOutlined
                                        placeholder={t('common.select')}
                                        icon={
                                            <Icon
                                                name={'essential-filter'}
                                                color={Colors.BASE_400}
                                            />
                                        }
                                        options={companyAttributes.map((attr) => ({
                                            ...attr,
                                            label: decodeAndSanitize(
                                                getOptionLabelText(attr.label),
                                            ),
                                        }))}
                                        onChange={onSelectCompanyAttribute}
                                        values={selectedCompanyAttributeOptions.map((attribute) =>
                                            decodeAndSanitize(attribute.name),
                                        )}
                                        sort={'none'}
                                    />
                                </div>

                                {selectedCompanyAttributeOptions.map((attribute) => (
                                    <AttributeSelectList
                                        key={attribute.name}
                                        attribute={attribute}
                                        filters={filters}
                                        selectCompanyAttributeOption={selectCompanyAttributeOption}
                                    />
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}
        </Card>
    );
};
